import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  Loader2, Sparkles, FileCheck2, Eye, Download, RotateCcw, Send, CheckCircle2, XCircle,
  AlertTriangle, ArrowUpRight, Ban, History, Wallet, ClipboardList, FileText, Package,
  Building2, Upload, Trash2, HelpCircle, BookOpen, Plus,
} from "lucide-react";
import { toast } from "sonner";
import { api, brl, fmtDateTime, errMsg } from "@/lib/api";

const STATUS_META = {
  rascunho: ["Rascunho", "bg-slate-100 text-slate-600"],
  em_revisao: ["Em revisão", "bg-sky-50 text-sky-700"],
  pendente_correcao: ["Pendente de correção", "bg-rose-50 text-rose-700"],
  pronta: ["Pronta para envio", "bg-emerald-50 text-emerald-700"],
  enviada: ["Enviada", "bg-indigo-50 text-indigo-700"],
  substituida: ["Substituída", "bg-amber-50 text-amber-700"],
  cancelada: ["Cancelada", "bg-slate-200 text-slate-500"],
};
const CANAIS = ["Portal de compras", "E-mail", "Plataforma eletrônica", "Outro"];

const _normTxt = (s) => (s || "").toLowerCase().replace(/[^0-9a-zà-ú ]/gi, "").replace(/\s+/g, " ").trim();
const _isSigLine = (s, names) => {
  const t = (s || "").trim();
  if (!t) return false;
  if (/_{3,}/.test(t) || /representante\s+legal/i.test(t) || /empres[áa]ri[oa]/i.test(t) || /assinatura/i.test(t)) return true;
  if (/\[preencher\]/i.test(t) && /\bde\b/i.test(t)) return true;
  if (t.replace(/[*[\]. ]/g, "").toLowerCase() === "preencher") return true;
  if (names.has(_normTxt(t))) return true;
  return false;
};
const cleanDeclBody = (body, titulo, campos = {}) => {
  const names = new Set(["razao_social", "nome_fantasia", "representante_legal", "resp_nome", "resp_cargo"].map((k) => _normTxt(campos[k])).filter(Boolean));
  const tnorm = _normTxt(titulo);
  let lines = (body || "").split("\n").filter((ln) => {
    const s = ln.trim();
    if (s.toUpperCase().includes("RASCUNHO GERADO POR IA")) return false;
    const head = s.replace(/^[#*]+/, "").replace(/\*+$/, "").trim();
    if (tnorm && _normTxt(head) === tnorm) return false;
    return true;
  });
  while (lines.length) {
    const s = lines[lines.length - 1].trim();
    if (s === "" || _isSigLine(s, names)) { lines.pop(); continue; }
    break;
  }
  return lines.join("\n").trim();
};
const _boldParse = (line) => line.split(/\*\*(.+?)\*\*/g).map((seg, i) => (i % 2 === 1 ? <strong key={i}>{seg}</strong> : seg));
function DeclBody({ text }) {
  return (text || "").split("\n").map((ln, i) => {
    let s = ln;
    let heading = false;
    if (s.trim().startsWith("#")) { s = s.replace(/^#+\s*/, ""); heading = true; }
    if (!s.trim()) return <div key={i} className="h-2" />;
    return <p key={i} className={`text-xs leading-relaxed text-slate-700 ${heading ? "font-bold" : ""}`}>{_boldParse(s)}</p>;
  });
}

const DECL_ST = {
  pendente: ["🔴", "Pendente", "bg-rose-50 text-rose-700 border-rose-200"],
  revisao: ["🟡", "Requer revisão", "bg-amber-50 text-amber-700 border-amber-200"],
  atendida: ["🟢", "Atendida", "bg-emerald-50 text-emerald-700 border-emerald-200"],
  nao_aplicavel: ["⚪", "Não aplicável", "bg-slate-50 text-slate-500 border-slate-200"],
  analise_humana: ["🟠", "Pendente de análise humana", "bg-orange-50 text-orange-700 border-orange-200"],
};
const EXTRA_CATS = ["Nota de Esclarecimento", "Declaração", "Catálogo", "Documento Complementar", "Comprovante", "Outro"];

const Field = ({ label, children, hint }) => (
  <div>
    <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-500">{label}</label>
    {children}
    {hint && <p className="mt-0.5 text-[10px] text-slate-400">{hint}</p>}
  </div>
);
const inputCls = "w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-sky-400 focus:outline-none";

export default function PropostaModule({ id, setParentTab, navigate }) {
  const [pp, setPp] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [assisting, setAssisting] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [envioModal, setEnvioModal] = useState(null);
  const [trechoModal, setTrechoModal] = useState(null);
  const [declTitulo, setDeclTitulo] = useState("");
  const [genDecl, setGenDecl] = useState(false);
  const [pacoteBusy, setPacoteBusy] = useState(false);
  const [extraForm, setExtraForm] = useState(null);
  const [editorDid, setEditorDid] = useState(null);
  const cfgInit = useRef(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/opportunities/${id}/proposta`);
      setPp(data);
      if (!cfgInit.current) { setCfg(data.config || {}); cfgInit.current = true; }
    } catch (e) { toast.error(errMsg(e)); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const assistSt = pp?.assist_status;
  const matrixSt = pp?.declaracoes_matrix?.status;
  useEffect(() => {
    if (assistSt === "processing" || matrixSt === "processing") {
      const t = setInterval(load, 4000);
      return () => clearInterval(t);
    }
  }, [assistSt, matrixSt, load]);

  if (!pp || !cfg) return <div className="grid place-items-center py-16"><Loader2 className="h-6 w-6 animate-spin text-sky-600" /></div>;

  const setC = (k, v) => setCfg((c) => ({ ...c, [k]: v }));
  const setItemCfg = (n, field, v) => setCfg((c) => ({ ...c, itens: { ...(c.itens || {}), [n]: { ...((c.itens || {})[n] || {}), [field]: v } } }));

  const saveCfg = async (extra = {}, action = "Configuração da proposta atualizada") => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/opportunities/${id}/proposta`, { config: cfg, ...extra, _action: action });
      setPp(data); toast.success("Salvo");
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };
  const runAssist = async () => {
    setAssisting(true);
    try { await api.post(`/opportunities/${id}/proposta/assist`); toast.info("Analisando o edital e preparando a proposta…"); load(); }
    catch (e) { toast.error(errMsg(e)); } finally { setAssisting(false); }
  };
  const gerarVersao = async () => {
    setGenerating(true);
    try { const { data } = await api.post(`/opportunities/${id}/proposta/versao`, {}); setPp(data); toast.success("Versão final gerada"); }
    catch (e) { toast.error(errMsg(e)); } finally { setGenerating(false); }
  };
  const restaurar = async (vid) => {
    try { const { data } = await api.post(`/opportunities/${id}/proposta/versao/${vid}/restaurar`); setPp(data); toast.success("Versão restaurada"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const cancelar = async () => {
    if (!window.confirm("Cancelar esta proposta?")) return;
    try { const { data } = await api.post(`/opportunities/${id}/proposta/cancelar`); setPp(data); } catch (e) { toast.error(errMsg(e)); }
  };
  const registrarEnvio = async () => {
    try { const { data } = await api.post(`/opportunities/${id}/proposta/enviar`, envioModal); setPp(data); setEnvioModal(null); toast.success("Envio registrado"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  // Declarações
  const analyzeDecl = async () => {
    try { await api.post(`/opportunities/${id}/declaracoes/analyze`); toast.info("A IA está lendo o edital para identificar as declarações…"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const setRowStatus = async (rid, status) => {
    try { const { data } = await api.patch(`/opportunities/${id}/declaracoes-matrix/${rid}`, { status }); setPp(data); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const gerarRascunhoRow = async (row) => {
    if (row.rascunho_id) { setEditorDid(row.rascunho_id); return; }
    try {
      const { data } = await api.post(`/opportunities/${id}/declaracoes/generate`, { matrix_row_id: row.id, titulo: row.nome });
      setPp(data);
      const r = (data.declaracoes_matrix?.rows || []).find((x) => x.id === row.id);
      if (r?.rascunho_id) setEditorDid(r.rascunho_id);
      toast.success("Rascunho gerado — abrindo editor");
    } catch (e) { toast.error(errMsg(e)); }
  };
  const saveDecl = async (did, patch) => {
    const { data } = await api.patch(`/opportunities/${id}/declaracoes/${did}`, patch);
    setPp(data); return data;
  };
  const genDeclPdf = async (did, addExtra) => {
    const { data } = await api.post(`/opportunities/${id}/declaracoes/${did}/pdf`, { add_to_extra: addExtra });
    setPp(data); toast.success(addExtra ? "PDF gerado e incluído no Pacote Final (seção Declarações)" : "PDF gerado");
    return data;
  };
  const gerarDeclaracaoManual = async () => {
    if (!declTitulo.trim()) { toast.error("Informe o título"); return; }
    setGenDecl(true);
    try { await api.post(`/opportunities/${id}/declaracoes/generate`, { titulo: declTitulo }); setDeclTitulo(""); load(); toast.success("Rascunho gerado"); }
    catch (e) { toast.error(errMsg(e)); } finally { setGenDecl(false); }
  };
  const delDeclaracao = async (did) => {
    try { const { data } = await api.delete(`/opportunities/${id}/declaracoes/${did}`); setPp(data); } catch (e) { toast.error(errMsg(e)); }
  };
  // Pacote
  const toggleSel = async (group, docId) => {
    const cur = pp.pacote_selecao?.[group] || [];
    const next = cur.includes(docId) ? cur.filter((x) => x !== docId) : [...cur, docId];
    try { const { data } = await api.patch(`/opportunities/${id}/pacote-selecao`, { [group]: next }); setPp(data); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const addExtra = async () => {
    if (!extraForm?.nome?.trim()) { toast.error("Informe o nome do documento"); return; }
    try {
      const { data } = await api.post(`/opportunities/${id}/documentos-extra`, extraForm);
      const created = (data.documentos_extra || []).find((e) => e.nome === extraForm.nome && !e.arquivo_id) || (data.documentos_extra || [])[0];
      if (extraForm._file && created) {
        const fd = new FormData(); fd.append("file", extraForm._file);
        await api.post(`/opportunities/${id}/documentos-extra/${created.id}/file`, fd);
      }
      setExtraForm(null); load(); toast.success("Documento adicional anexado");
    } catch (e) { toast.error(errMsg(e)); }
  };
  const toggleExtraPacote = async (did, val) => {
    try { const { data } = await api.patch(`/opportunities/${id}/documentos-extra/${did}`, { incluir_pacote: val }); setPp(data); } catch (e) { toast.error(errMsg(e)); }
  };
  const delExtra = async (did) => {
    try { const { data } = await api.delete(`/opportunities/${id}/documentos-extra/${did}`); setPp(data); } catch (e) { toast.error(errMsg(e)); }
  };
  const gerarPacote = async () => {
    setPacoteBusy(true);
    try { const { data } = await api.post(`/opportunities/${id}/pacote/gerar`); setPp(data); toast.success("Pacote final gerado"); }
    catch (e) { toast.error(errMsg(e)); } finally { setPacoteBusy(false); }
  };

  const gotoPendencia = (target) => {
    if (target === "empresa") { navigate("/configuracoes"); return; }
    if (target === "documentos") { document.getElementById("prop-sec-decl")?.scrollIntoView({ behavior: "smooth" }); return; }
    if (target === "declaracoes") { document.getElementById("prop-sec-decl")?.scrollIntoView({ behavior: "smooth" }); return; }
    if (target === "itens") { setParentTab && setParentTab("itens"); return; }
    document.getElementById("prop-sec-config")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const [stLabel, stCls] = STATUS_META[pp.status] || STATUS_META.rascunho;
  const fin = pp.financeiro, chk = pp.check, d = pp.data;
  const cv = pp.company_validation || { ok: true, faltando: [] };
  const matrix = pp.declaracoes_matrix || {};
  const matrixRows = matrix.rows || [];
  const openPreview = () => {
    if (!cv.ok) { setShowPreview(false); toast.error("Complete os dados da empresa antes de visualizar"); document.getElementById("prop-cv-banner")?.scrollIntoView({ behavior: "smooth" }); return; }
    setShowPreview((s) => !s);
  };

  return (
    <div data-testid="ws-proposta" className="space-y-5">
      {/* Cabeçalho / ações */}
      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2">
              <div className="text-sm font-semibold text-slate-800">Proposta Comercial</div>
              <span data-testid="ws-prop-status" className={`rounded-full px-2.5 py-0.5 text-[11px] font-bold ${stCls}`}>{stLabel}</span>
            </div>
            <p className="mt-1 max-w-2xl text-xs text-slate-500">Montada com os dados do Workspace (itens, preços, empresa) e adaptada ao edital pela IA. <strong>Você revisa e envia manualmente.</strong> Dados internos (fornecedor, custo, margem, canal, logística) NUNCA entram no documento.</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button data-testid="ws-prop-assist" onClick={runAssist} disabled={assisting || assistSt === "processing"} className="flex items-center gap-2 rounded-lg border border-sky-200 bg-sky-50 px-3 py-2 text-sm font-semibold text-sky-700 disabled:opacity-60">
              {(assisting || assistSt === "processing") ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} Analisar edital (IA)
            </button>
            <button data-testid="ws-prop-preview" onClick={openPreview} className="flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">
              <Eye className="h-4 w-4" /> {showPreview ? "Ocultar prévia" : "Visualizar proposta"}
            </button>
          </div>
        </div>
        {assistSt === "processing" && <div className="mt-3 rounded-lg border border-sky-200 bg-sky-50 px-3 py-2 text-sm text-sky-700">Assistente analisando o edital… atualiza sozinho.</div>}
        {pp.leak?.length > 0 && <div className="mt-3 flex items-start gap-2 rounded-lg border border-rose-300 bg-rose-50 px-3 py-2 text-sm text-rose-700"><Ban className="mt-0.5 h-4 w-4 shrink-0" /><span>Bloqueio: dados internos no texto: <strong>{pp.leak.join(", ")}</strong>. Remova antes de gerar.</span></div>}
      </div>

      {/* Validação dos dados da empresa */}
      {!cv.ok && (
        <div id="prop-cv-banner" data-testid="ws-prop-cv-banner" className="rounded-xl border border-rose-300 bg-rose-50 p-4">
          <div className="flex items-center gap-2 text-sm font-bold text-rose-700"><Building2 className="h-4 w-4" /> 🔴 DADOS DA EMPRESA INCOMPLETOS</div>
          <p className="mt-1 text-xs text-rose-600">Falta preencher: {cv.faltando.map((f) => f.label).join(", ")}. A proposta usa o cadastro central — complete em Configurações.</p>
          <button data-testid="ws-prop-ir-config" onClick={() => navigate("/configuracoes")} className="mt-2 inline-flex items-center gap-1 rounded-lg bg-rose-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-rose-700">Ir para Configurações <ArrowUpRight className="h-3.5 w-3.5" /></button>
        </div>
      )}

      {/* Condições */}
      <div id="prop-sec-config" className="rounded-xl border border-slate-200 bg-white p-4">
        <div className="mb-3 flex items-center justify-between">
          <div className="text-sm font-semibold text-slate-800">Condições da proposta</div>
          <button data-testid="ws-prop-save-cfg" onClick={() => saveCfg()} disabled={saving} className="rounded-lg bg-sky-600 px-4 py-1.5 text-sm font-semibold text-white disabled:opacity-60">{saving ? "Salvando…" : "Salvar"}</button>
        </div>
        <div className="mb-3">
          <Field label="Apresentação (texto institucional)" hint="Gerado pela IA no botão acima; edite livremente.">
            <textarea data-testid="ws-prop-apresentacao" rows={3} value={cfg.apresentacao ?? ""} onChange={(e) => setC("apresentacao", e.target.value)} className={inputCls} />
          </Field>
        </div>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <Field label="Prazo de entrega"><input data-testid="ws-prop-prazo" value={cfg.prazo_entrega ?? ""} onChange={(e) => setC("prazo_entrega", e.target.value)} className={inputCls} /></Field>
          <Field label="Local de entrega"><input value={cfg.local_entrega ?? ""} onChange={(e) => setC("local_entrega", e.target.value)} className={inputCls} /></Field>
          <Field label="Validade da proposta (dias)" hint="Obrigatório para finalizar."><input data-testid="ws-prop-validade" type="number" value={cfg.validade_dias ?? ""} onChange={(e) => setC("validade_dias", e.target.value ? Number(e.target.value) : null)} className={inputCls} /></Field>
          <Field label="Forma / condições de pagamento"><input value={cfg.forma_pagamento ?? ""} onChange={(e) => setC("forma_pagamento", e.target.value)} className={inputCls} /></Field>
          <Field label="Garantia"><input value={cfg.garantia ?? ""} onChange={(e) => setC("garantia", e.target.value)} className={inputCls} /></Field>
          <Field label="Condições de recebimento"><input value={cfg.condicoes_recebimento ?? ""} onChange={(e) => setC("condicoes_recebimento", e.target.value)} className={inputCls} /></Field>
        </div>
        <div className="mt-3"><Field label="Observações"><textarea rows={2} value={cfg.observacoes ?? ""} onChange={(e) => setC("observacoes", e.target.value)} className={inputCls} /></Field></div>
        <div className="mt-3 flex flex-wrap gap-4">
          <label className="flex items-center gap-2 text-sm text-slate-700"><input data-testid="ws-prop-toggle-banco" type="checkbox" checked={!!cfg.incluir_dados_bancarios} onChange={(e) => setC("incluir_dados_bancarios", e.target.checked)} className="h-4 w-4 rounded" /> Incluir dados bancários</label>
          <label className="flex items-center gap-2 text-sm text-slate-700"><input type="checkbox" checked={!!cfg.incluir_declaracao_conformidade} onChange={(e) => setC("incluir_declaracao_conformidade", e.target.checked)} className="h-4 w-4 rounded" /> Incluir declaração de conformidade</label>
        </div>
      </div>

      {/* Itens */}
      <div className="rounded-xl border border-slate-200 bg-white p-4" id="prop-sec-itens">
        <div className="mb-2 flex items-center justify-between">
          <div className="text-sm font-semibold text-slate-800">Proposta de preços</div>
          <button onClick={() => saveCfg()} disabled={saving} className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50">Salvar itens</button>
        </div>
        <p className="mb-3 text-xs text-slate-500">Preço vem da Precificação; marca/modelo puxam da cotação escolhida do item. Sobrescreva aqui se necessário. Em branco = usar valor calculado (mostrado como referência).</p>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b border-slate-200 text-left text-[11px] uppercase text-slate-400">
              <th className="py-2 pr-2">Item</th><th className="pr-2">Descrição</th><th className="pr-2">Marca</th><th className="pr-2">Modelo/Ref.</th><th className="pr-2 text-right">Qtde</th><th className="pr-2 text-right">Vlr unit.</th><th className="pr-2 text-right">Vlr total</th>
            </tr></thead>
            <tbody>
              {pp.itens.map((i) => {
                const n = String(i.numero_item); const ic = (cfg.itens || {})[n] || {};
                return (
                  <tr key={n} data-testid="ws-prop-item-row" className="border-b border-slate-100 align-top">
                    <td className="py-2 pr-2 font-semibold text-slate-700">{i.numero_item}</td>
                    <td className="max-w-[220px] pr-2 text-xs text-slate-600">{i.descricao}</td>
                    <td className="pr-2"><input value={ic.marca ?? ""} placeholder={i.marca || "—"} onChange={(e) => setItemCfg(n, "marca", e.target.value)} className="w-24 rounded border border-slate-200 px-2 py-1 text-xs" /></td>
                    <td className="pr-2"><input value={ic.modelo ?? ""} placeholder={i.modelo || "—"} onChange={(e) => setItemCfg(n, "modelo", e.target.value)} className="w-24 rounded border border-slate-200 px-2 py-1 text-xs" /></td>
                    <td className="pr-2 text-right text-slate-600">{i.quantidade ?? "—"} {i.unidade}</td>
                    <td className="pr-2 text-right"><input type="number" value={ic.valor_unitario ?? ""} placeholder={i.valor_unitario || "0"} onChange={(e) => setItemCfg(n, "valor_unitario", e.target.value)} className="w-24 rounded border border-slate-200 px-2 py-1 text-right text-xs" /></td>
                    <td className="pr-2 text-right font-semibold text-slate-800">{brl(i.valor_total)}</td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot><tr><td colSpan={6} className="py-2 pr-2 text-right text-sm font-bold text-slate-700">VALOR GLOBAL</td><td className="pr-2 text-right text-base font-bold text-sky-700" data-testid="ws-prop-global">{brl(d.valor_global)}</td></tr></tfoot>
          </table>
        </div>
      </div>

      {/* Financeiro interno */}
      <div className="rounded-xl border border-amber-200 bg-amber-50/40 p-4" data-testid="ws-prop-financeiro">
        <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-amber-900"><Wallet className="h-4 w-4" /> Validação financeira <span className="rounded bg-amber-200 px-1.5 py-0.5 text-[10px] font-bold text-amber-900">USO INTERNO — não vai para o PDF</span></div>
        <div className="mb-3 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
          {[["Valor de referência", brl(fin.valor_referencia)], ["Valor da proposta", brl(fin.valor_proposta)], ["Desconto", `${fin.desconto_pct}%`], ["Custo total interno", fin.tem_custo ? brl(fin.custo_total) : "—"], ["Lucro estimado", fin.tem_custo ? brl(fin.lucro) : "—"], ["Margem", fin.tem_custo ? `${fin.margem_pct}%` : "—"]].map(([lbl, val], idx) => (
            <div key={idx} className="rounded-lg bg-white p-2 text-center"><div className="text-[10px] font-semibold uppercase text-slate-400">{lbl}</div><div className="text-sm font-bold text-slate-800">{val}</div></div>
          ))}
        </div>
      </div>

      {/* Check final */}
      <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="ws-prop-check">
        <div className="mb-3 flex items-center justify-between">
          <div className="text-sm font-semibold text-slate-800">Check final da proposta</div>
          <span className={`rounded-full px-2.5 py-0.5 text-[11px] font-bold ${chk.ok_to_generate ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700"}`}>{chk.ok_to_generate ? "Pronta para gerar" : `${chk.blockers.length} bloqueio(s)`}</span>
        </div>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {chk.groups.map((g) => (
            <div key={g.nome}>
              <div className="mb-1.5 text-[11px] font-bold uppercase tracking-wide text-slate-400">{g.nome}</div>
              <div className="space-y-1.5">
                {g.itens.map((c, idx) => (
                  <div key={idx} className="flex items-start gap-2 text-xs">
                    {c.ok ? <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-500" /> : (c.blocker ? <XCircle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-rose-500" /> : <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-500" />)}
                    <div>
                      <span className={c.ok ? "text-slate-600" : "font-semibold text-slate-800"}>{c.label}</span>
                      {!c.ok && c.pendencia && <div className="text-[11px] text-rose-600">{c.pendencia}</div>}
                      {!c.ok && c.target && <button data-testid="ws-prop-corrigir" onClick={() => gotoPendencia(c.target)} className="mt-0.5 inline-flex items-center gap-0.5 text-[11px] font-semibold text-sky-600 hover:underline">Corrigir pendência <ArrowUpRight className="h-3 w-3" /></button>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
        <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-slate-100 pt-3">
          <button data-testid="ws-prop-gerar-final" onClick={gerarVersao} disabled={generating || !chk.ok_to_generate || pp.leak?.length > 0} className="flex items-center gap-2 rounded-lg bg-gradient-to-r from-sky-600 to-emerald-600 px-4 py-2 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50">
            {generating ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileCheck2 className="h-4 w-4" />} Gerar versão da proposta
          </button>
          <a data-testid="ws-prop-preview-pdf" href={`${api.defaults.baseURL}/opportunities/${id}/proposta/preview/pdf`} target="_blank" rel="noreferrer" className="flex items-center gap-2 rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"><Eye className="h-4 w-4" /> Abrir PDF de prévia</a>
          {pp.status !== "cancelada" && <button onClick={cancelar} className="flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-500 hover:bg-slate-50"><Ban className="h-4 w-4" /> Cancelar</button>}
          {!chk.ok_to_generate && <span className="text-xs text-rose-500">Corrija os bloqueios para liberar a geração.</span>}
        </div>
      </div>

      {showPreview && <PropostaPreview d={d} />}

      {/* Versões da proposta */}
      <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="ws-prop-versoes">
        <div className="mb-3 text-sm font-semibold text-slate-800">Versões da proposta</div>
        {pp.versoes.length === 0 ? <p className="text-sm text-slate-400">Nenhuma versão gerada ainda.</p> : (
          <div className="space-y-2">
            {pp.versoes.map((v) => (
              <div key={v.id} data-testid="ws-prop-versao" className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-slate-100 p-3">
                <div>
                  <div className="text-sm font-semibold text-slate-700">Versão {v.numero} <span className={`ml-1 rounded-full px-2 py-0.5 text-[10px] font-bold ${(STATUS_META[v.status] || STATUS_META.pronta)[1]}`}>{(STATUS_META[v.status] || STATUS_META.pronta)[0]}</span></div>
                  <div className="text-[11px] text-slate-400">{fmtDateTime(v.criada_em)} · {v.criada_por} · {brl(v.valor_global)}{v.obs ? ` · ${v.obs}` : ""}</div>
                </div>
                <div className="flex gap-2">
                  <a data-testid="ws-prop-pdf" href={`${api.defaults.baseURL}/opportunities/${id}/proposta/versao/${v.id}/pdf`} target="_blank" rel="noreferrer" className="flex items-center gap-1 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"><Download className="h-3.5 w-3.5" /> PDF</a>
                  <button onClick={() => restaurar(v.id)} className="flex items-center gap-1 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50"><RotateCcw className="h-3.5 w-3.5" /> Restaurar</button>
                  <button data-testid="ws-prop-enviar" onClick={() => setEnvioModal({ versao_id: v.id, canal: "Portal de compras", obs: "" })} className="flex items-center gap-1 rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-indigo-700"><Send className="h-3.5 w-3.5" /> Marcar enviada</button>
                </div>
              </div>
            ))}
          </div>
        )}
        {pp.envio && <div className="mt-3 rounded-lg border border-indigo-100 bg-indigo-50/50 p-3 text-xs text-indigo-800" data-testid="ws-prop-envio-info">Enviada em {fmtDateTime(pp.envio.enviada_em)} · versão {pp.envio.versao_numero} · canal: {pp.envio.canal} · por {pp.envio.usuario}{pp.envio.obs ? ` · ${pp.envio.obs}` : ""}</div>}
      </div>

      {/* Declarações — Matriz inteligente */}
      <DeclaracoesMatrix
        id={id} pp={pp} matrix={matrix} matrixRows={matrixRows} matrixSt={matrixSt}
        analyzeDecl={analyzeDecl} setRowStatus={setRowStatus} gerarRascunhoRow={gerarRascunhoRow}
        delDeclaracao={delDeclaracao} declTitulo={declTitulo} setDeclTitulo={setDeclTitulo}
        genDecl={genDecl} gerarDeclaracaoManual={gerarDeclaracaoManual} setTrechoModal={setTrechoModal}
      />

      {/* Documentos para envio + Pacote Final */}
      <DocumentosPacote
        id={id} pp={pp} cv={cv} chk={chk} matrixRows={matrixRows}
        toggleSel={toggleSel} extraForm={extraForm} setExtraForm={setExtraForm} addExtra={addExtra}
        toggleExtraPacote={toggleExtraPacote} delExtra={delExtra} gerarPacote={gerarPacote} pacoteBusy={pacoteBusy}
      />

      {/* Auditoria */}
      <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="ws-prop-audit">
        <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-slate-800"><History className="h-4 w-4" /> Histórico da proposta</div>
        {(pp.audit || []).length === 0 ? <p className="text-sm text-slate-400">Sem registros.</p> : (
          <ul className="space-y-1.5">{pp.audit.slice(0, 30).map((a, idx) => (
            <li key={idx} className="flex items-start gap-2 text-xs text-slate-600"><span className="mt-0.5 text-slate-300">•</span><span><strong className="text-slate-700">{a.action}</strong>{a.detail ? ` — ${a.detail}` : ""} <span className="text-slate-400">· {fmtDateTime(a.ts)} · {a.user}</span></span></li>
          ))}</ul>
        )}
      </div>

      {/* Modal envio */}
      {envioModal && (
        <Modal onClose={() => setEnvioModal(null)} testid="ws-prop-envio-modal" title="Registrar envio" subtitle="Gerar o PDF e enviar são eventos diferentes. Registre só após enviar pelo canal oficial.">
          <Field label="Canal de envio"><select data-testid="ws-prop-envio-canal" value={envioModal.canal} onChange={(e) => setEnvioModal({ ...envioModal, canal: e.target.value })} className={inputCls}>{CANAIS.map((c) => <option key={c} value={c}>{c}</option>)}</select></Field>
          <div className="mt-3"><Field label="Observação"><input value={envioModal.obs} onChange={(e) => setEnvioModal({ ...envioModal, obs: e.target.value })} className={inputCls} /></Field></div>
          <div className="mt-5 flex justify-end gap-2"><button onClick={() => setEnvioModal(null)} className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600">Cancelar</button><button data-testid="ws-prop-envio-save" onClick={registrarEnvio} className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white">Registrar</button></div>
        </Modal>
      )}

      {/* Modal ver trecho */}
      {trechoModal && (
        <Modal onClose={() => setTrechoModal(null)} testid="ws-prop-trecho-modal" title="Trecho do edital" subtitle={trechoModal.nome}>
          {trechoModal.fonte ? (
            <div className="space-y-2 text-sm">
              <div className="flex flex-wrap gap-2 text-xs">
                {trechoModal.fonte.arquivo && <span className="rounded bg-slate-100 px-2 py-0.5">Arquivo: {trechoModal.fonte.arquivo}</span>}
                {trechoModal.fonte.pagina && <span className="rounded bg-slate-100 px-2 py-0.5">Página {trechoModal.fonte.pagina}</span>}
                {trechoModal.fonte.item && <span className="rounded bg-slate-100 px-2 py-0.5">Item {trechoModal.fonte.item}</span>}
              </div>
              <blockquote className="border-l-4 border-sky-300 bg-slate-50 p-3 text-sm italic text-slate-700">{trechoModal.fonte.trecho || "Trecho não informado pela IA."}</blockquote>
            </div>
          ) : <p className="text-sm text-slate-500">Fonte não disponível (edital não anexado ou não identificado). Confirme manualmente no edital.</p>}
          <div className="mt-5 flex justify-end"><button onClick={() => setTrechoModal(null)} className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600">Fechar</button></div>
        </Modal>
      )}

      {editorDid && (
        <DeclaracaoEditor key={editorDid} id={id}
          entry={(pp.declaracoes || []).find((x) => x.id === editorDid)}
          row={(matrixRows || []).find((r) => r.rascunho_id === editorDid)}
          saveDecl={saveDecl} genDeclPdf={genDeclPdf} onClose={() => setEditorDid(null)} />
      )}
    </div>
  );
}

const DECL_DOC_ST = {
  rascunho_gerado: ["Rascunho gerado", "bg-slate-100 text-slate-600"],
  em_revisao: ["Em revisão", "bg-amber-50 text-amber-700"],
  pdf_gerado: ["PDF gerado", "bg-sky-50 text-sky-700"],
  disponivel: ["Documento disponível", "bg-emerald-50 text-emerald-700"],
};
const CAMPO_LABELS = {
  razao_social: "Razão Social", nome_fantasia: "Nome Fantasia", cnpj: "CNPJ", endereco: "Endereço",
  cidade: "Cidade", uf: "UF", cep: "CEP", representante_legal: "Representante legal",
  resp_nome: "Responsável (nome)", resp_cpf: "CPF", resp_cargo: "Cargo/Função",
  telefone: "Telefone", email: "E-mail", local: "Local", data: "Data",
};

function DeclaracaoEditor({ id, entry, row, saveDecl, genDeclPdf, onClose }) {
  const [body, setBody] = useState(entry?.conteudo || "");
  const [campos, setCampos] = useState(entry?.campos || {});
  const [prev, setPrev] = useState(false);
  const [busy, setBusy] = useState(false);
  if (!entry) return null;
  const [stLbl, stCls] = DECL_DOC_ST[entry.status] || DECL_DOC_ST.rascunho_gerado;
  const setK = (k, v) => setCampos((c) => ({ ...c, [k]: v }));
  const doSave = async () => { setBusy(true); try { await saveDecl(entry.id, { conteudo: body, campos }); toast.success("Salvo"); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); } };
  const doPdf = async (addExtra) => {
    setBusy(true);
    try {
      await saveDecl(entry.id, { conteudo: body, campos });
      await genDeclPdf(entry.id, addExtra);
      if (!addExtra) window.open(`${api.defaults.baseURL}/opportunities/${id}/declaracoes/${entry.id}/pdf`, "_blank");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const bodyClean = cleanDeclBody(body, entry.titulo, campos);
  return (
    <div className="fixed inset-0 z-50 grid place-items-center p-4">
      <div className="absolute inset-0 bg-slate-900/60" onClick={onClose} />
      <div className="relative flex max-h-[90vh] w-full max-w-4xl flex-col rounded-xl bg-white shadow-2xl" data-testid="ws-decl-editor">
        <div className="border-b border-slate-100 p-5">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="flex items-center gap-2 text-base font-bold text-slate-800"><FileText className="h-5 w-5 text-sky-600" /> Editor de declaração</div>
              <div className="mt-1 text-sm font-semibold text-slate-700">{entry.titulo}</div>
              {row?.fonte && <div className="text-[11px] text-slate-500">Fonte: Edital{row.fonte.item ? ` — Item ${row.fonte.item}` : ""}{row.fonte.pagina ? ` — Página ${row.fonte.pagina}` : ""}</div>}
            </div>
            <span className={`rounded-full px-2.5 py-0.5 text-[11px] font-bold ${stCls}`}>{stLbl}</span>
          </div>
        </div>
        <div className="grid flex-1 gap-4 overflow-y-auto p-5 lg:grid-cols-2">
          <div>
            <div className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-500">Texto da declaração</div>
            {prev ? (
              <div className="rounded-lg border border-slate-300 bg-white p-4 text-sm shadow-inner" data-testid="ws-decl-editor-preview">
                <div className="text-center font-bold text-slate-900">{(entry.titulo || "").toUpperCase()}</div>
                <p className="mt-3 text-xs text-slate-700"><strong>{campos.razao_social}</strong>{campos.cnpj ? `, CNPJ ${campos.cnpj}` : ""}{campos.endereco ? `, ${campos.endereco}` : ""}:</p>
                <div className="mt-2"><DeclBody text={bodyClean} /></div>
                {(campos.local || campos.data) && (
                  <p className="mt-6 text-xs text-slate-700">{[campos.local, campos.data].filter(Boolean).join(", ")}.</p>
                )}
                <div className="mt-10 text-center text-xs text-slate-700">
                  _______________________________<br />
                  <strong>{campos.resp_nome || campos.representante_legal}</strong>
                  {campos.resp_cpf ? <><br />CPF: {campos.resp_cpf}</> : null}
                  {campos.resp_cargo ? <><br />{campos.resp_cargo}</> : null}
                </div>
              </div>
            ) : (
              <textarea data-testid="ws-decl-editor-body" value={body} onChange={(e) => setBody(e.target.value)} rows={16} className="w-full rounded-lg border border-slate-200 p-3 font-mono text-xs" />
            )}
          </div>
          <div>
            <div className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-500">Dados (preenchidos do cadastro · editáveis só neste documento)</div>
            <div className="grid grid-cols-2 gap-2">
              {Object.keys(CAMPO_LABELS).map((k) => (
                <div key={k} className={["endereco", "razao_social"].includes(k) ? "col-span-2" : ""}>
                  <label className="mb-0.5 block text-[10px] font-semibold text-slate-500">{CAMPO_LABELS[k]}</label>
                  <input data-testid={`ws-decl-campo-${k}`} value={campos[k] ?? ""} onChange={(e) => setK(k, e.target.value)} className="w-full rounded border border-sky-100 bg-sky-50/50 px-2 py-1.5 text-xs" />
                </div>
              ))}
            </div>
            <div className="mt-3 rounded-lg bg-amber-50 p-2 text-[11px] text-amber-700">Editar aqui NÃO altera o cadastro oficial (Configurações). Vale só para este documento.</div>
            {(entry.versoes || []).length > 0 && (
              <div className="mt-3">
                <div className="mb-1 text-[11px] font-bold uppercase text-slate-500">Versões</div>
                {entry.versoes.map((v) => (
                  <a key={v.numero} data-testid="ws-decl-versao" href={`${api.defaults.baseURL}/opportunities/${id}/declaracoes/${entry.id}/versao/${v.numero}/pdf`} target="_blank" rel="noreferrer" className="flex items-center justify-between rounded border border-slate-100 px-2 py-1 text-xs text-slate-600 hover:bg-slate-50">
                    <span>V{v.numero} · {fmtDateTime(v.gerada_em)}</span><Download className="h-3.5 w-3.5" />
                  </a>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className="flex flex-wrap items-center justify-between gap-2 border-t border-slate-100 p-4">
          <div className="text-[11px] text-amber-700">Rascunho — revise e assine antes do envio. Nunca há assinatura automática.</div>
          <div className="flex flex-wrap gap-2">
            <button data-testid="ws-decl-editor-save" onClick={doSave} disabled={busy} className="rounded-lg border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700 disabled:opacity-60">Salvar</button>
            <button data-testid="ws-decl-editor-prev" onClick={() => setPrev((p) => !p)} className="flex items-center gap-1 rounded-lg border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700"><Eye className="h-4 w-4" /> {prev ? "Editar" : "Visualizar prévia"}</button>
            <button data-testid="ws-decl-editor-pdf" onClick={() => doPdf(false)} disabled={busy} className="flex items-center gap-1 rounded-lg bg-sky-600 px-3 py-2 text-sm font-semibold text-white disabled:opacity-60">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} Gerar PDF</button>
            <button data-testid="ws-decl-editor-pdf-extra" onClick={() => doPdf(true)} disabled={busy} className="flex items-center gap-1 rounded-lg bg-gradient-to-r from-indigo-600 to-sky-600 px-3 py-2 text-sm font-semibold text-white disabled:opacity-60"><Plus className="h-4 w-4" /> Gerar PDF e incluir no Pacote</button>
            <button data-testid="ws-decl-editor-close" onClick={onClose} className="rounded-lg border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-500">Fechar</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Modal({ children, onClose, title, subtitle, testid }) {
  return (
    <div className="fixed inset-0 z-50 grid place-items-center p-4">
      <div className="absolute inset-0 bg-slate-900/50" onClick={onClose} />
      <div className="relative w-full max-w-lg rounded-xl bg-white p-6 shadow-2xl" data-testid={testid}>
        <h3 className="mb-1 font-display text-lg font-bold">{title}</h3>
        {subtitle && <p className="mb-4 text-xs text-slate-500">{subtitle}</p>}
        {children}
      </div>
    </div>
  );
}

function DeclaracoesMatrix({ id, pp, matrix, matrixRows, matrixSt, analyzeDecl, setRowStatus, gerarRascunhoRow, delDeclaracao, declTitulo, setDeclTitulo, genDecl, gerarDeclaracaoManual, setTrechoModal }) {
  const [expand, setExpand] = useState({});
  const declById = Object.fromEntries((pp.declaracoes || []).map((x) => [x.id, x]));
  return (
    <div id="prop-sec-decl" className="rounded-xl border border-slate-200 bg-white p-4" data-testid="ws-declaracoes">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <div className="flex items-center gap-2 text-sm font-semibold text-slate-800"><ClipboardList className="h-4 w-4" /> Declarações necessárias</div>
          <p className="text-xs text-slate-500">A IA lê o edital e identifica as declarações exigidas, com fonte (item/página) e status. Rascunhos são gerados por IA — <strong>revise antes de assinar</strong>, nunca há assinatura automática.</p>
        </div>
        <button data-testid="ws-decl-analyze" onClick={analyzeDecl} disabled={matrixSt === "processing"} className="flex items-center gap-2 rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-2 text-sm font-semibold text-indigo-700 disabled:opacity-60">
          {matrixSt === "processing" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} Identificar declarações (IA)
        </button>
      </div>
      {matrixSt === "processing" && <div className="mt-3 rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-2 text-sm text-indigo-700">A IA está lendo o edital…</div>}
      {matrixSt === "error" && <div className="mt-3 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">Falha: {matrix.error}</div>}
      {matrix.nota && <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700">{matrix.nota}</div>}

      <div className="mt-3 space-y-2">
        {matrixRows.length === 0 && matrixSt !== "processing" && (
          <p className="text-sm text-slate-400">Nenhuma declaração identificada ainda. Anexe o edital (aba Documentos) e clique em <strong>Identificar declarações (IA)</strong>.</p>
        )}
        {matrixRows.map((r) => {
          const [emoji, lbl, cls] = DECL_ST[r.status] || DECL_ST.pendente;
          const draft = r.rascunho_id ? declById[r.rascunho_id] : null;
          const isOpen = expand[r.id];
          return (
            <div key={r.id} data-testid="ws-decl-row" className={`rounded-lg border p-3 ${cls}`}>
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 text-sm font-semibold text-slate-800">{emoji} {r.nome}</div>
                  {r.descricao && <div className="mt-0.5 text-xs text-slate-600">{r.descricao}</div>}
                  <div className="mt-1 flex flex-wrap gap-2 text-[11px] text-slate-500">
                    <span className="rounded bg-white/70 px-2 py-0.5 font-semibold">{lbl}</span>
                    {r.motivo && <span className="rounded bg-white/70 px-2 py-0.5">Motivo: {r.motivo}</span>}
                    {r.fonte?.pagina && <span className="rounded bg-white/70 px-2 py-0.5">📄 Pág. {r.fonte.pagina}{r.fonte.item ? ` · Item ${r.fonte.item}` : ""}</span>}
                  </div>
                </div>
                <select data-testid="ws-decl-status" value={r.status} onChange={(e) => setRowStatus(r.id, e.target.value)} className="rounded-lg border border-slate-200 bg-white px-2 py-1 text-xs">
                  {Object.entries(DECL_ST).map(([k, v]) => <option key={k} value={k}>{v[0]} {v[1]}</option>)}
                </select>
              </div>
              <div className="mt-2 flex flex-wrap gap-2">
                <button data-testid="ws-decl-gerar-rascunho" onClick={() => gerarRascunhoRow(r)} className="flex items-center gap-1 rounded-lg bg-slate-900 px-3 py-1.5 text-xs font-semibold text-white"><Sparkles className="h-3.5 w-3.5" /> {draft ? "Abrir editor" : "Gerar rascunho"}</button>
                {draft && <span className="rounded-lg bg-emerald-100 px-2 py-1 text-[11px] font-bold text-emerald-700">Rascunho gerado{draft.arquivo_id ? " · PDF pronto" : ""}</span>}
                <button data-testid="ws-decl-ver-trecho" onClick={() => setTrechoModal(r)} className="flex items-center gap-1 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700"><BookOpen className="h-3.5 w-3.5" /> Ver trecho do edital</button>
                <button onClick={() => setExpand((x) => ({ ...x, [r.id]: !x[r.id] }))} className="flex items-center gap-1 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700"><HelpCircle className="h-3.5 w-3.5" /> Como resolver</button>
              </div>
              {isOpen && (
                <div className="mt-2 rounded-lg bg-white/80 p-3 text-xs text-slate-600">
                  <div className="font-semibold text-slate-700">Como resolver:</div>
                  <ol className="ml-4 list-decimal space-y-0.5">
                    <li>Gerar o rascunho da declaração.</li><li>Revisar os dados e o texto.</li>
                    <li>Assinar (responsável legal).</li><li>Anexar o documento assinado no Cofre/Documentos adicionais.</li>
                    <li>Marcar o status como <strong>Atendida</strong>.</li>
                  </ol>
                </div>
              )}
              {draft && <pre className="mt-2 max-h-40 overflow-y-auto whitespace-pre-wrap rounded bg-white/80 p-2 font-sans text-[11px] text-slate-600">{draft.conteudo}</pre>}
            </div>
          );
        })}
      </div>

      <div className="mt-4 border-t border-slate-100 pt-3">
        <div className="mb-1 text-xs font-semibold text-slate-500">Adicionar declaração manualmente</div>
        <div className="flex gap-2">
          <input data-testid="ws-decl-titulo" value={declTitulo} onChange={(e) => setDeclTitulo(e.target.value)} placeholder="Ex.: Declaração de elaboração independente" className="flex-1 rounded-lg border border-slate-200 px-3 py-2 text-sm" />
          <button data-testid="ws-decl-generate" onClick={gerarDeclaracaoManual} disabled={genDecl} className="flex items-center gap-2 rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white disabled:opacity-60">{genDecl ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} Gerar rascunho</button>
        </div>
        {(pp.declaracoes || []).filter((x) => !x.matrix_row_id).length > 0 && (
          <div className="mt-3 space-y-2">
            {pp.declaracoes.filter((x) => !x.matrix_row_id).map((dc) => (
              <div key={dc.id} data-testid="ws-decl-item" className="rounded-lg border border-amber-200 bg-amber-50/50 p-3">
                <div className="flex items-center justify-between"><div className="text-sm font-semibold text-slate-700">{dc.titulo}</div><button onClick={() => delDeclaracao(dc.id)} className="text-xs font-semibold text-rose-600 hover:underline">Excluir</button></div>
                <div className="mt-1 inline-block rounded bg-amber-100 px-2 py-0.5 text-[10px] font-bold text-amber-800">RASCUNHO GERADO POR IA — REVISÃO HUMANA NECESSÁRIA</div>
                <pre className="mt-2 max-h-40 overflow-y-auto whitespace-pre-wrap font-sans text-[11px] text-slate-600">{dc.conteudo}</pre>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function DocumentosPacote({ id, pp, cv, chk, matrixRows, toggleSel, extraForm, setExtraForm, addExtra, toggleExtraPacote, delExtra, gerarPacote, pacoteBusy }) {
  const sel = pp.pacote_selecao || {};
  const declSel = sel.declaracoes || [], cofreSel = sel.cofre || [], extraSel = sel.extras || [];
  const declPend = matrixRows.filter((r) => ["pendente", "analise_humana"].includes(r.status)).length;
  const declOk = matrixRows.length - declPend;
  const canGerar = cv.ok && chk.ok_to_generate;
  const Group = ({ title, count, children }) => (
    <div className="rounded-lg border border-slate-100 p-3">
      <div className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-500">{title} <span className="text-slate-400">({count})</span></div>
      {children}
    </div>
  );
  const Chk = ({ checked, onChange, label, sub, testid }) => (
    <label className="flex items-start gap-2 py-1 text-sm" data-testid={testid}>
      <input type="checkbox" checked={checked} onChange={onChange} className="mt-0.5 h-4 w-4 rounded" />
      <span><span className="text-slate-700">{label}</span>{sub && <span className="block text-[11px] text-slate-400">{sub}</span>}</span>
    </label>
  );
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="ws-pacote">
      <div className="flex items-center gap-2 text-sm font-semibold text-slate-800"><Package className="h-4 w-4" /> Documentos para envio & Pacote Final</div>
      <p className="mb-3 text-xs text-slate-500">A <strong>prévia</strong> mostra só a proposta comercial. O <strong>Pacote Final</strong> consolida a proposta + os documentos selecionados num único PDF. Nada é incluído sem sua marcação; os originais do Cofre nunca são alterados.</p>

      {/* Documentos adicionais */}
      <div className="mb-4 rounded-lg border border-slate-100 p-3">
        <div className="mb-2 flex items-center justify-between">
          <div className="text-xs font-bold uppercase tracking-wide text-slate-500">Documentos adicionais</div>
          <button data-testid="ws-extra-add" onClick={() => setExtraForm({ categoria: "Outro", incluir_pacote: false })} className="flex items-center gap-1 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"><Plus className="h-3.5 w-3.5" /> Anexar documento</button>
        </div>
        {(pp.documentos_extra || []).length === 0 ? <p className="text-xs text-slate-400">Nenhum documento adicional.</p> : (
          <div className="space-y-1.5">{pp.documentos_extra.map((e) => (
            <div key={e.id} data-testid="ws-extra-item" className="flex flex-wrap items-center justify-between gap-2 rounded border border-slate-100 p-2 text-sm">
              <div className="min-w-0"><span className="font-medium text-slate-700">{e.nome}</span> <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] text-slate-500">{e.categoria}</span>{e.descricao && <span className="block text-[11px] text-slate-400">{e.descricao}</span>}</div>
              <div className="flex items-center gap-2">
                {e.arquivo_id ? <a href={`${api.defaults.baseURL}/opportunities/${id}/documentos-extra/${e.id}/file`} target="_blank" rel="noreferrer" className="text-slate-400 hover:text-sky-600"><Download className="h-4 w-4" /></a> : <span className="text-[10px] text-amber-600">sem arquivo</span>}
                <button onClick={() => delExtra(e.id)} className="text-slate-400 hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
          ))}</div>
        )}
      </div>

      {/* Seleção por grupos */}
      <div className="grid gap-3 lg:grid-cols-2">
        <Group title="1 · Proposta Comercial" count={1}>
          <div className="flex items-center gap-2 py-1 text-sm text-slate-600"><CheckCircle2 className="h-4 w-4 text-emerald-500" /> Sempre incluída (última versão/prévia)</div>
        </Group>
        <Group title="2 · Declarações" count={(pp.declaracoes || []).length}>
          {(pp.declaracoes || []).length === 0 ? <p className="text-xs text-slate-400">Nenhum rascunho gerado.</p> : pp.declaracoes.map((dc) => (
            <Chk key={dc.id} testid="ws-pacote-decl" checked={declSel.includes(dc.id)} onChange={() => toggleSel("declaracoes", dc.id)} label={dc.titulo} />
          ))}
        </Group>
        <Group title="3 · Documentos de habilitação (Cofre)" count={(pp.cofre || []).length}>
          {(pp.cofre || []).length === 0 ? <p className="text-xs text-slate-400">Nenhum documento no Cofre.</p> : pp.cofre.map((cd) => (
            <Chk key={cd.id} testid="ws-pacote-cofre" checked={cofreSel.includes(cd.id)} onChange={() => toggleSel("cofre", cd.id)} label={cd.nome} sub={`${cd.sicaf_nivel || "—"} · ${cd.status === "vencido" ? "⚠ vencido" : cd.status === "proximo" ? "vence em breve" : "válido"}`} />
          ))}
        </Group>
        <Group title="4 · Documentos adicionais" count={(pp.documentos_extra || []).length}>
          {(pp.documentos_extra || []).length === 0 ? <p className="text-xs text-slate-400">Nenhum.</p> : pp.documentos_extra.map((e) => (
            <Chk key={e.id} testid="ws-pacote-extra" checked={extraSel.includes(e.id) || e.incluir_pacote} onChange={() => toggleSel("extras", e.id)} label={e.nome} sub={e.categoria} />
          ))}
        </Group>
      </div>

      {/* Check final do pacote */}
      <div className="mt-4 rounded-lg border border-slate-200 bg-slate-50 p-3" data-testid="ws-pacote-check">
        <div className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-500">Check final do envio</div>
        <div className="grid gap-1.5 sm:grid-cols-2">
          <ResumoItem ok={chk.ok_to_generate} label="Proposta comercial" detail={chk.ok_to_generate ? "OK" : `${chk.blockers.length} pendência(s)`} />
          <ResumoItem ok={cv.ok} label="Dados da empresa/representante" detail={cv.ok ? "OK" : cv.faltando.map((f) => f.label).join(", ")} />
          <ResumoItem ok={declPend === 0} warn={declPend > 0} label="Declarações" detail={matrixRows.length ? `${declOk} ok · ${declPend} pendente(s)` : "nenhuma identificada"} />
          <ResumoItem ok label="Documentos selecionados" detail={`${declSel.length + cofreSel.length + extraSel.length + (pp.documentos_extra || []).filter((e) => e.incluir_pacote).length} selecionado(s)`} />
        </div>
        <div className="mt-2 text-sm font-bold">{canGerar ? <span className="text-emerald-600">PACOTE PRONTO PARA GERAÇÃO</span> : <span className="text-rose-600">EXISTEM PENDÊNCIAS — REVISAR ANTES DE GERAR</span>}</div>
      </div>

      <div className="mt-3">
        <button data-testid="ws-pacote-gerar" onClick={gerarPacote} disabled={pacoteBusy || !canGerar} className="flex items-center gap-2 rounded-lg bg-gradient-to-r from-indigo-600 to-sky-600 px-4 py-2 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50">
          {pacoteBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Package className="h-4 w-4" />} Gerar proposta final (pacote)
        </button>
      </div>

      {/* Histórico de pacotes */}
      {(pp.pacotes || []).length > 0 && (
        <div className="mt-4 space-y-2" data-testid="ws-pacote-versoes">
          <div className="text-xs font-bold uppercase tracking-wide text-slate-500">Pacotes gerados</div>
          {pp.pacotes.map((p) => (
            <div key={p.id} data-testid="ws-pacote-versao" className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-slate-100 p-3">
              <div><div className="text-sm font-semibold text-slate-700">Pacote V{p.numero} · {p.count} documento(s)</div><div className="text-[11px] text-slate-400">{fmtDateTime(p.criado_em)} · {p.criado_por} · {brl(p.valor_global)}</div></div>
              <a data-testid="ws-pacote-pdf" href={`${api.defaults.baseURL}/opportunities/${id}/pacote/${p.id}/pdf`} target="_blank" rel="noreferrer" className="flex items-center gap-1 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"><Download className="h-3.5 w-3.5" /> Baixar PDF</a>
            </div>
          ))}
        </div>
      )}

      {extraForm && (
        <Modal onClose={() => setExtraForm(null)} testid="ws-extra-modal" title="Anexar documento adicional">
          <div className="space-y-3">
            <Field label="Nome do documento"><input data-testid="ws-extra-nome" value={extraForm.nome ?? ""} onChange={(e) => setExtraForm({ ...extraForm, nome: e.target.value })} className={inputCls} /></Field>
            <Field label="Categoria"><select data-testid="ws-extra-cat" value={extraForm.categoria} onChange={(e) => setExtraForm({ ...extraForm, categoria: e.target.value })} className={inputCls}>{EXTRA_CATS.map((c) => <option key={c} value={c}>{c}</option>)}</select></Field>
            <Field label="Descrição"><input value={extraForm.descricao ?? ""} onChange={(e) => setExtraForm({ ...extraForm, descricao: e.target.value })} className={inputCls} /></Field>
            <Field label="Observação"><input value={extraForm.observacao ?? ""} onChange={(e) => setExtraForm({ ...extraForm, observacao: e.target.value })} className={inputCls} /></Field>
            <Field label="Arquivo"><input data-testid="ws-extra-file" type="file" onChange={(e) => setExtraForm({ ...extraForm, _file: e.target.files?.[0] })} className="text-xs" /></Field>
            <label className="flex items-center gap-2 text-sm text-slate-700"><input type="checkbox" checked={!!extraForm.incluir_pacote} onChange={(e) => setExtraForm({ ...extraForm, incluir_pacote: e.target.checked })} className="h-4 w-4 rounded" /> Incluir no pacote final</label>
          </div>
          <div className="mt-5 flex justify-end gap-2"><button onClick={() => setExtraForm(null)} className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600">Cancelar</button><button data-testid="ws-extra-save" onClick={addExtra} className="rounded-lg bg-sky-600 px-4 py-2 text-sm font-semibold text-white">Salvar</button></div>
        </Modal>
      )}
    </div>
  );
}

function ResumoItem({ ok, warn, label, detail }) {
  return (
    <div className="flex items-start gap-2 text-xs">
      {ok ? <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-500" /> : (warn ? <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-500" /> : <XCircle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-rose-500" />)}
      <span><span className="font-semibold text-slate-700">{label}</span> <span className="text-slate-500">— {detail}</span></span>
    </div>
  );
}

function Row({ k, v }) {
  return <div className="flex gap-2 py-0.5 text-xs"><span className="w-40 shrink-0 font-semibold text-slate-500">{k}</span><span className="text-slate-700">{v || "—"}</span></div>;
}

function PropostaPreview({ d }) {
  const c = d.cabecalho, p = d.proponente, cond = d.condicoes, r = d.responsavel;
  return (
    <div className="rounded-xl border border-slate-300 bg-white p-6 shadow-inner" data-testid="ws-prop-preview-doc">
      <div className="mb-4 border-b-2 border-sky-600 pb-3"><div className="text-lg font-bold text-slate-900">{p.razao_social}</div><div className="text-xs text-slate-500">CNPJ: {p.cnpj || "—"}</div></div>
      <div className="text-center text-base font-bold text-slate-900">{c.titulo}</div>
      <div className="mt-2 grid grid-cols-2 gap-x-6 rounded-lg bg-slate-50 p-3">
        <Row k="Nº Licitação/Dispensa" v={c.licitacao_numero} /><Row k="Processo" v={c.processo} /><Row k="Órgão" v={c.orgao} /><Row k="Modalidade" v={c.modalidade} />
      </div>
      <div className="mt-4 text-sm font-bold text-sky-700">IDENTIFICAÇÃO DA PROPONENTE</div>
      <Row k="Endereço" v={p.endereco} /><Row k="Cidade/UF" v={p.cidade_uf} /><Row k="Telefone" v={p.telefone} /><Row k="E-mail" v={p.email} /><Row k="Representante legal" v={p.representante_legal} />
      {d.apresentacao && <><div className="mt-4 text-sm font-bold text-sky-700">1. APRESENTAÇÃO</div><p className="whitespace-pre-wrap text-xs text-slate-700">{d.apresentacao}</p></>}
      <div className="mt-4 text-sm font-bold text-sky-700">2. OBJETO</div><p className="text-xs text-slate-700">{c.objeto}</p>
      <div className="mt-4 text-sm font-bold text-sky-700">3. PROPOSTA DE PREÇOS</div>
      <div className="overflow-x-auto">
        <table className="mt-1 w-full text-[11px]">
          <thead><tr className="bg-sky-600 text-left text-white"><th className="p-1">Item</th><th className="p-1">Descrição</th><th className="p-1">Marca</th><th className="p-1">Modelo/Ref.</th><th className="p-1">Un.</th><th className="p-1 text-right">Qtd</th><th className="p-1 text-right">Vlr un.</th><th className="p-1 text-right">Vlr total</th></tr></thead>
          <tbody>{d.itens.map((i, idx) => (<tr key={idx} className={idx % 2 ? "bg-slate-50" : ""}><td className="p-1">{i.numero_item}</td><td className="p-1">{i.descricao}</td><td className="p-1">{i.marca || "—"}</td><td className="p-1">{i.modelo || i.referencia || "—"}</td><td className="p-1">{i.unidade}</td><td className="p-1 text-right">{i.quantidade}</td><td className="p-1 text-right">{brl(i.valor_unitario)}</td><td className="p-1 text-right">{brl(i.valor_total)}</td></tr>))}</tbody>
        </table>
      </div>
      <div className="mt-1 flex justify-between rounded bg-slate-900 px-3 py-2 text-sm font-bold text-white"><span>VALOR GLOBAL DA PROPOSTA</span><span>{brl(d.valor_global)}</span></div>
      <div className="mt-4 text-sm font-bold text-sky-700">CONDIÇÕES</div>
      <Row k="Prazo de entrega" v={cond.prazo_entrega} /><Row k="Local de entrega" v={cond.local_entrega} /><Row k="Validade" v={cond.validade_dias ? `${cond.validade_dias} dias` : ""} /><Row k="Pagamento" v={cond.forma_pagamento} /><Row k="Garantia" v={cond.garantia} />
      <div className="mt-4 text-sm font-bold text-sky-700">RESPONSÁVEL</div>
      <Row k="Nome" v={r.nome} /><Row k="CPF" v={r.cpf} /><Row k="Cargo" v={r.cargo} />
      <div className="mt-8 text-center text-xs text-slate-500">_______________________________________<br />{r.nome || "Responsável"} · {r.cargo}</div>
    </div>
  );
}
